// order-history/components/order-list/order-list.component.tsx
import { useCallback, useEffect, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { OrderCardComponent } from '../shared/order-card/order-card.component'
import type { Order } from '../../types/order.type'

export interface OrderListProps {
	orders: Order[]
	isTransmissionPage?: boolean
	selectedOrderId?: string
	ordersSelected?: string[]
	onSelectOrder?: (order: Order) => void
	onViewOrder?: (orderId: number) => void
	onToggleOrderSelection?: (orderId: string) => void
	hasMore?: boolean
	isLoadingMore?: boolean
	onLoadMore?: () => void
}

export function OrderList({
	orders,
	isTransmissionPage = false,
	selectedOrderId,
	ordersSelected = [],
	onSelectOrder,
	onViewOrder,
	onToggleOrderSelection,
	hasMore = false,
	isLoadingMore = false,
	onLoadMore,
}: OrderListProps) {
	const lastOrderRef = useRef<HTMLDivElement | null>(null)

	// Garantir que orders seja sempre um array
	const safeOrders = Array.isArray(orders) ? orders : []

	// Intersection Observer para scroll infinito
	useEffect(() => {
		if (!hasMore || isLoadingMore || !onLoadMore || !lastOrderRef.current || safeOrders.length === 0) {
			return
		}

		const observer = new IntersectionObserver(
			(entries) => {
				const lastEntry = entries[0]
				if (lastEntry.isIntersecting) {
					console.log('🔄 Último pedido visível, carregando mais...')
					onLoadMore()
				}
			},
			{
				threshold: 0.1,
				rootMargin: '100px'
			}
		)

		observer.observe(lastOrderRef.current)

		return () => {
			observer.disconnect()
		}
	}, [hasMore, isLoadingMore, onLoadMore, safeOrders.length])

	const handleSelectOrder = useCallback((order: Order) => {
		onSelectOrder?.(order)
	}, [onSelectOrder])

	const handleViewOrder = useCallback((orderId: number) => {
		onViewOrder?.(orderId)
	}, [onViewOrder])

	const handleToggleSelection = useCallback((orderId: string) => {
		onToggleOrderSelection?.(orderId)
	}, [onToggleOrderSelection])

	if (safeOrders.length === 0 && !isLoadingMore) {
		return (
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 mt-2">
				<div className="col-span-full text-center py-8">
					<p className="text-sm text-gray-500">Nenhum pedido encontrado</p>
				</div>
			</div>
		)
	}

	return (
		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 mt-2">
			{safeOrders.map((order, index) => {
				const isLastItem = index === safeOrders.length - 1
				const isInSelectionArray = ordersSelected.includes(String(order.idPedidoCel))

				return (
					<div
						key={`${order.idPedidoCel}${order.idPedido}${order.fkUsuarioCel}`}
						ref={isLastItem ? lastOrderRef : undefined}
					>
						<OrderCardComponent
							order={order}
							index={index}
							isTransmissionPage={isTransmissionPage}
							onSelect={handleSelectOrder}
							onView={handleViewOrder}
							onToggleSelection={handleToggleSelection}
							isInSelectionArray={isInSelectionArray}
						/>
					</div>
				)
			})}

			{isLoadingMore && (
				<div className="col-span-full flex justify-center items-center py-4">
					<Loader2 className="animate-spin h-6 w-6 text-primary" />
					<span className="ml-2 text-sm text-gray-600">Carregando mais pedidos...</span>
				</div>
			)}
		</div>
	)
}